import { Field, Form, Formik } from 'formik';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { array, lazy, object, string } from 'yup';

import {
  useBulkCreateDiscounts,
  UseBulkCreateDiscountsData,
  UseBulkCreateDiscountsVars,
} from '@frontend/api';
import {
  Box,
  Button,
  DrawerBody,
  DrawerButtonGroup,
  DrawerFooter,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from '@frontend/components';

import { receiveComponents } from 'modules/quotes/quoteItems/data/actions';

import { getQuoteId } from 'store/quotes/selectors';

import { notifyError, notifySuccess } from 'utils/notifyUtils';
import { pluralize } from 'utils/stringUtils';

import { quoteItemSelect } from '../data/actions';
import { DiscountsTable } from './DiscountsTable';
import { ReplacePopconfirm } from './ReplacePopconfirm';
import * as actions from './data/actions';
import { getComponents } from './data/reducer';
import type { FormValues } from './types';

const messages = {
  ACTION: 'To update discounts, select an action',
  DISCOUNTS: 'To add discounts, first select one or more discounts to add',
};

const initialValues: FormValues = {
  action: 'add',
  discounts: [],
};

export const BulkEditDiscountsDrawerForm = () => {
  const quoteId = useSelector(getQuoteId);
  const components = useSelector(getComponents);

  const dispatch = useDispatch();
  const onClose = () => dispatch(actions.close());

  const { mutate: bulkCreateDiscounts, isLoading } = useBulkCreateDiscounts({
    queryOptions: {
      onSuccess: (response, requestData) => {
        const mappedResponse = response.reduce<
          Record<string, UseBulkCreateDiscountsData[number]>
        >((ax, dx) => ({ ...ax, [dx.id]: dx }), {});

        dispatch(
          receiveComponents(
            components.map((component) => ({
              ...component,
              ...mappedResponse[component.id],
            })),
          ),
        );
        dispatch(quoteItemSelect.deselectAll());
        notifySuccess(
          requestData.deleteExistingDiscounts
            ? 'Replaced discounts'
            : `Added ${pluralize('discount', requestData.discounts.length)}`,
        );
        onClose();
      },
      onError: (_, requestData) => {
        notifyError(
          `Failed to ${
            requestData.deleteExistingDiscounts
              ? 'replace discounts'
              : `add ${pluralize('discount', requestData.discounts.length)}`
          }`,
        );
      },
    },
  });

  const onSubmit = useCallback(
    (values: FormValues) =>
      bulkCreateDiscounts({
        quoteId,
        components: components.map(({ id }) => id),
        deleteExistingDiscounts: values.action === 'replace',
        discounts: values.discounts.map((discount) => {
          const res: UseBulkCreateDiscountsVars['discounts'][number] = {
            name: discount.name,
          };

          if (discount.discountDefId != null) {
            res.discountDefId = discount.discountDefId;
          }

          if (discount.manualPercentage != null) {
            const manualPercentage = Number(discount.manualPercentage);
            if (!isNaN(manualPercentage)) {
              res.manualPercentage = manualPercentage;
            }
          }

          return res;
        }),
      }),
    [bulkCreateDiscounts, quoteId, components],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={lazy((values) =>
        object().shape({
          action: string().required(messages.ACTION),
          discounts:
            values.action === 'add'
              ? array().required(messages.DISCOUNTS).min(1, messages.DISCOUNTS)
              : array().required(messages.DISCOUNTS),
        }),
      )}
      onSubmit={onSubmit}
    >
      {({ setFieldValue, errors, dirty, isValid, values }) => {
        const commonButtonProps = {
          children: 'Update Discounts',
          isLoading: isLoading,
          isDisabled: !dirty || !isValid,
        };

        return (
          <>
            <DrawerBody
              as={Form}
              id="bulk-edit-discounts-form"
              noValidate
              autoComplete="off"
              display="flex"
              flexDir="column"
              gap={3}
            >
              <FormControl isRequired>
                <FormLabel>Action</FormLabel>
                <Field
                  name="action"
                  as={RadioGroup}
                  onChange={(v: string) => setFieldValue('action', v)}
                >
                  <Radio value="add">Add discounts</Radio>
                  <Radio value="replace">Replace all discounts</Radio>
                </Field>
              </FormControl>
              <DiscountsTable />
            </DrawerBody>
            <DrawerFooter>
              <DrawerButtonGroup>
                <Button variant="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Tooltip
                  label={
                    !dirty
                      ? messages.DISCOUNTS
                      : errors.action ?? errors.discounts
                  }
                >
                  {values.action === 'add' ? (
                    <Button
                      form="bulk-edit-discounts-form"
                      type="submit"
                      {...commonButtonProps}
                    />
                  ) : (
                    <Box>
                      <ReplacePopconfirm
                        itemCount={components.length}
                        onConfirm={() => onSubmit(values)}
                      >
                        <Button {...commonButtonProps} />
                      </ReplacePopconfirm>
                    </Box>
                  )}
                </Tooltip>
              </DrawerButtonGroup>
            </DrawerFooter>
          </>
        );
      }}
    </Formik>
  );
};
